import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  message,
  Popconfirm,
  Tag,
  Modal,
  Descriptions,
  Row,
  Col,
  Statistic,
  Divider,
  Input,
  Select,
  DatePicker,
  Alert,
  Tooltip,
  Badge
} from 'antd';
import {
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { userService } from '../../services';
import { getUserInfo } from '../../utils/helpers';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PendingApproval = () => {
  const [pendingList, setPendingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set());
  const [userInfo] = useState(getUserInfo());
  
  // Filters
  const [searchKeyword, setSearchKeyword] = useState('');
  const [genderFilter, setGenderFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [filteredData, setFilteredData] = useState([]);

  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    male: 0,
    female: 0,
    thisMonth: 0
  });

  useEffect(() => {
    loadPendingList();
  }, []);

  // Filter data when search or filters change
  useEffect(() => {
    let filtered = [...pendingList];

    // Search by name, phone, or email
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(item => 
        item.full_name?.toLowerCase().includes(keyword) ||
        item.phone?.toLowerCase().includes(keyword) ||
        item.email?.toLowerCase().includes(keyword)
      );
    }

    // Filter by gender
    if (genderFilter) {
      filtered = filtered.filter(item => item.gender === genderFilter);
    }

    // Filter by date range
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter(item => {
        const birthDate = new Date(item.birth_date);
        return birthDate >= startDate.toDate() && birthDate <= endDate.toDate();
      });
    }

    setFilteredData(filtered);
  }, [pendingList, searchKeyword, genderFilter, dateRange]);

  // Calculate statistics
  useEffect(() => {
    const total = pendingList.length;
    const male = pendingList.filter(item => item.gender === 'Nam').length;
    const female = pendingList.filter(item => item.gender === 'Nữ').length;
    
    const thisMonth = pendingList.filter(item => {
      const birthDate = new Date(item.birth_date);
      const now = new Date();
      return birthDate.getMonth() === now.getMonth();
    }).length;

    setStatistics({ total, male, female, thisMonth });
  }, [pendingList]);

  const loadPendingList = async () => {
    try {
      setLoading(true);
      const response = await userService.getPendingNhomdaily();
      
      if (response.success) {
        const data = response.data?.pending_list || [];
        setPendingList(data);
        setFilteredData(data);
        
        if (data.length === 0) {
          message.info('🎉 Không có nhân sự nào cần duyệt!');
        }
      } else {
        throw new Error(response.message || 'Không thể tải danh sách');
      }
    } catch (error) {
      console.error('Error loading pending list:', error);
      message.error(`Lỗi tải danh sách: ${error.message}`);
      setPendingList([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, fullName) => {
    try {
      setProcessingIds(prev => new Set([...prev, userId]));
      
      const response = await userService.approveEmployee(userId);
      
      if (response.success) {
        message.success(`✅ Đã duyệt thành công nhân viên: ${fullName}`);
        // Remove from list after approval
        setPendingList(prev => prev.filter(item => item.user_id !== userId));
      } else {
        throw new Error(response.message || 'Duyệt thất bại');
      }
    } catch (error) {
      console.error('Error approving employee:', error);
      
      // Xử lý các loại lỗi cụ thể
      let errorMessage = '';
      if (error.response?.status === 404) {
        errorMessage = 'API chưa được triển khai trên server. Vui lòng liên hệ admin.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Lỗi server nội bộ. Vui lòng thử lại sau.';
      } else if (error.message.includes('Request failed')) {
        errorMessage = 'Không thể kết nối tới server. Kiểm tra kết nối mạng.';
      } else {
        errorMessage = error.message;
      }
      
      message.error(`❌ Lỗi duyệt nhân viên ${fullName}: ${errorMessage}`);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleReject = async (userId, fullName) => {
    try {
      setProcessingIds(prev => new Set([...prev, userId]));
      
      const response = await userService.rejectEmployee(userId);
      
      if (response.success) {
        message.warning(`⚠️ Đã hủy duyệt nhân viên: ${fullName}`);
        // Remove from list after rejection
        setPendingList(prev => prev.filter(item => item.user_id !== userId));
      } else {
        throw new Error(response.message || 'Hủy duyệt thất bại');
      }
    } catch (error) {
      console.error('Error rejecting employee:', error);
      
      // Xử lý các loại lỗi cụ thể
      let errorMessage = '';
      if (error.response?.status === 404) {
        errorMessage = 'API chưa được triển khai trên server. Vui lòng liên hệ admin.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Lỗi server nội bộ. Vui lòng thử lại sau.';
      } else if (error.message.includes('Request failed')) {
        errorMessage = 'Không thể kết nối tới server. Kiểm tra kết nối mạng.';
      } else {
        errorMessage = error.message;
      }
      
      message.error(`❌ Lỗi hủy duyệt nhân viên ${fullName}: ${errorMessage}`);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleBulkApprove = () => {
    Modal.confirm({
      title: 'Duyệt tất cả nhân sự',
      content: `Bạn có chắc chắn muốn duyệt tất cả ${filteredData.length} nhân sự đang hiển thị?`,
      icon: <TeamOutlined />,
      okText: 'Duyệt tất cả',
      cancelText: 'Hủy',
      okButtonProps: { danger: false, type: 'primary' },
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        
        for (const item of filteredData) {
          try {
            await userService.approveEmployee(item.user_id);
            successCount++;
          } catch (error) {
            console.error(`Error approving ${item.full_name}:`, error);
            failCount++;
          }
        }
        
        if (successCount > 0) {
          message.success(`✅ Đã duyệt thành công ${successCount} nhân sự`);
        }
        if (failCount > 0) {
          message.error(`❌ ${failCount} nhân sự duyệt thất bại`);
        }
        
        // Reload list
        loadPendingList();
      }
    });
  };

  const showDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const clearFilters = () => {
    setSearchKeyword('');
    setGenderFilter(null);
    setDateRange(null);
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => (
        <Text strong style={{ color: '#1890ff' }}>
          {index + 1}
        </Text>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
      render: (id) => (
        <Tag color="blue">
          {id}
        </Tag>
      ),
    },
    {
      title: 'Thông tin cá nhân',
      key: 'personal_info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
            <UserOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
            {record.full_name}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <CalendarOutlined style={{ marginRight: '4px' }} />
            {new Date(record.birth_date).toLocaleDateString('vi-VN')}
            <Tag 
              color={record.gender === 'Nam' ? 'blue' : 'pink'} 
              size="small" 
              style={{ marginLeft: '8px' }}
            >
              {record.gender}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Liên hệ',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: '4px', fontSize: '12px' }}>
            <PhoneOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
            <Text copyable={{ text: record.phone }}>
              {record.phone}
            </Text>
          </div>
          <div style={{ fontSize: '12px' }}>
            <MailOutlined style={{ marginRight: '4px', color: '#fa8c16' }} />
            <Text copyable={{ text: record.email }}>
              {record.email}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Tuổi',
      key: 'age',
      width: 80,
      render: (_, record) => {
        const birthDate = new Date(record.birth_date);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        return (
          <Tag color={age < 25 ? 'green' : age < 35 ? 'blue' : 'orange'}>
            {age} tuổi
          </Tag>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        const isProcessing = processingIds.has(record.user_id);
        return (
          <Space size="small">
            <Tooltip title="Xem chi tiết">
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => showDetail(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Duyệt nhân viên"
              description={`Bạn có chắc chắn muốn duyệt nhân viên "${record.full_name}"?`}
              onConfirm={() => handleApprove(record.user_id, record.full_name)}
              okText="Duyệt"
              cancelText="Hủy"
              okButtonProps={{ type: 'primary' }}
            >
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={isProcessing}
                disabled={isProcessing}
              >
                Duyệt
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Hủy duyệt nhân viên"
              description={`Bạn có chắc chắn muốn hủy duyệt nhân viên "${record.full_name}"?`}
              onConfirm={() => handleReject(record.user_id, record.full_name)}
              okText="Hủy duyệt"
              cancelText="Không"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                loading={isProcessing}
                disabled={isProcessing}
              >
                Từ chối
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Check permissions
  const canApprove = userInfo && ['admin', 'manager'].includes(userInfo.role?.toLowerCase());

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={3} style={{ marginBottom: '8px' }}>
            <TeamOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
            Nhân Sự cần Duyệt
          </Title>
          <Text type="secondary">
            Quản lý danh sách nhân viên đang chờ duyệt vào nhóm đại lý
          </Text>
        </div>

        {/* Permission Check */}
        {!canApprove && (
          <Alert
            message="Không đủ quyền"
            description="Bạn không có quyền duyệt nhân sự. Chỉ Admin và Manager mới có quyền này."
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        {/* API Status Notice */}
        <Alert
          message="Thông báo về tính năng"
          description={
            <div>
              <p>⚠️ <strong>API duyệt/từ chối nhân viên đang được phát triển.</strong></p>
              <p>• Nếu gặp lỗi 404: API chưa được triển khai trên server</p>
              <p>• Nếu gặp lỗi 401: Token đã hết hạn, vui lòng đăng nhập lại</p>
              <p>• Nếu gặp lỗi 500: Server đang gặp sự cố, vui lòng thử lại sau</p>
              <p>📞 Liên hệ admin nếu cần hỗ trợ</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
          closable
        />

        {/* Statistics */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Tổng số chờ duyệt"
                value={statistics.total}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Nam giới"
                value={statistics.male}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Nữ giới"
                value={statistics.female}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#eb2f96' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Sinh nhật tháng này"
                value={statistics.thisMonth}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Search
                placeholder="Tìm theo tên, SĐT, email..."
                allowClear
                prefix={<SearchOutlined />}
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Giới tính"
                allowClear
                value={genderFilter}
                onChange={setGenderFilter}
                style={{ width: '100%' }}
              >
                <Option value="Nam">Nam</Option>
                <Option value="Nữ">Nữ</Option>
              </Select>
            </Col>
            <Col span={6}>
              <RangePicker
                placeholder={['Từ ngày sinh', 'Đến ngày sinh']}
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <Space>
                <Button
                  icon={<FilterOutlined />}
                  onClick={clearFilters}
                >
                  Xóa bộ lọc
                </Button>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={loadPendingList}
                  loading={loading}
                >
                  Tải lại
                </Button>
                {canApprove && filteredData.length > 0 && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleBulkApprove}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Duyệt tất cả ({filteredData.length})
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Results Info */}
        {searchKeyword || genderFilter || dateRange ? (
          <Alert
            message={`Hiển thị ${filteredData.length} / ${statistics.total} kết quả`}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        ) : null}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="user_id"
          loading={loading}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} nhân sự`,
          }}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <TeamOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div>Không có nhân sự nào cần duyệt</div>
                <Text type="secondary">Tất cả nhân sự đã được duyệt hoặc chưa có đơn đăng ký mới</Text>
              </div>
            )
          }}
          rowClassName={(record, index) => {
            return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
          }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={
          <div>
            <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Chi tiết nhân viên: {selectedRecord?.full_name}
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Đóng
          </Button>,
          ...(canApprove ? [
            <Button
              key="reject"
              danger
              icon={<CloseOutlined />}
              onClick={() => {
                handleReject(selectedRecord?.user_id, selectedRecord?.full_name);
                setDetailModalVisible(false);
              }}
            >
              Từ chối
            </Button>,
            <Button
              key="approve"
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => {
                handleApprove(selectedRecord?.user_id, selectedRecord?.full_name);
                setDetailModalVisible(false);
              }}
            >
              Duyệt
            </Button>
          ] : [])
        ]}
        width={600}
      >
        {selectedRecord && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID người dùng">
              <Badge count={selectedRecord.user_id} style={{ backgroundColor: '#1890ff' }} />
            </Descriptions.Item>
            <Descriptions.Item label="Họ và tên">
              <Text strong style={{ fontSize: '16px' }}>
                {selectedRecord.full_name}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Ngày sinh">
              <Text>
                <CalendarOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                {new Date(selectedRecord.birth_date).toLocaleDateString('vi-VN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tuổi">
              {(() => {
                const birthDate = new Date(selectedRecord.birth_date);
                const today = new Date();
                const age = today.getFullYear() - birthDate.getFullYear();
                return (
                  <Tag color={age < 25 ? 'green' : age < 35 ? 'blue' : 'orange'} style={{ fontSize: '14px' }}>
                    {age} tuổi
                  </Tag>
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Giới tính">
              <Tag color={selectedRecord.gender === 'Nam' ? 'blue' : 'pink'} style={{ fontSize: '14px' }}>
                {selectedRecord.gender}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Số điện thoại">
              <Text copyable={{ text: selectedRecord.phone }}>
                <PhoneOutlined style={{ marginRight: '6px', color: '#52c41a' }} />
                {selectedRecord.phone}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <Text copyable={{ text: selectedRecord.email }}>
                <MailOutlined style={{ marginRight: '6px', color: '#fa8c16' }} />
                {selectedRecord.email}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Custom CSS */}
      <style jsx>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </div>
  );
};

export default PendingApproval;